import {
  Button,
  HStack,
  Stack,
  ModalCloseButton,
  Text,
  Input,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  Modal,
  Collapse,
  Spacer
} from '@chakra-ui/react';
import { addYears } from 'date-fns';
import { useForm } from 'react-hook-form';
import * as Sentry from '@sentry/react';
import pluralize from 'pluralize';
import { useQueryClient } from '@tanstack/react-query';

import { SFAddCircleIcon, SFSquareAndPencil } from '@assets/icons';
import { useUserStore } from '@store/app.store';
import { useStyledToast } from '@util/hooks';
import {
  useCreateInitiative,
  usePatchInitiative
} from '@services/query-services/initiatives.query-service';
import { useSetCardCollections } from '@services/query-services/cards.query-service';
import StyledLink from '@components/general/styled-link.component';

/**
 * @param {Object} props
 * @param {Initiative} props.editingCollection - collection to update title for
 * @param {Card[]} props.cards
 */
const CreateCollectionModal = ({ editingCollection, cards = [], disclosure }) => {
  const { mutateAsync: createInitiative, isLoading: isCreatingInitiative } = useCreateInitiative();
  const { mutateAsync: patchInitiative, isLoading: isPatchingInitiative } = usePatchInitiative();
  const { mutateAsync: setCardCollections, isLoading: isSettingCardCollections } =
    useSetCardCollections();
  const queryClient = useQueryClient();
  const user = useUserStore((state) => state.user);
  const toast = useStyledToast();
  const isEditing = !!editingCollection?.id;

  const {
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      title: editingCollection?.title ?? ''
    }
  });
  const maxTitleChars = 50;
  const title = watch('title');

  const onSubmit = async (values) => {
    let newCollection;
    try {
      if (isEditing) {
        await patchInitiative({ id: editingCollection?.id, title: values.title.trim() });
      } else {
        newCollection = await createInitiative({
          type: 'Collection',
          visibility: 'Collaborators',
          title: values.title.trim(),
          description: `This is a collection titled ${title} originally created by ${user.name}`,
          start_at: Date.now(),
          end_at: addYears(Date.now(), 10).getTime()
        });

        for (const card of cards) {
          const newCardCollections = [...(card.collections || []), newCollection];
          setCardCollections({
            id: card?.id,
            ...(newCardCollections?.length > 0 ? { body: newCardCollections } : {}),
            optimisticUpdateValue: newCardCollections
          });
        }
      }
    } catch (e) {
      if (e.response.data.errors.includes('The title is already in use by another Initiative.')) {
        toast({
          title: 'Title is already in use.',
          status: 'error',
          duration: 9000,
          isClosable: true
        });
      } else {
        Sentry.withScope((scope) => {
          scope.setContext('collection', { isEditing, collectionId: editingCollection?.id });
          scope.setExtra('error_message', `Error ${isEditing ? 'editing' : 'creating'} Collection`);
          Sentry.captureException(e);
        });
        toast({
          title: `Something went wrong while ${
            isEditing ? 'editing' : 'creating'
          } Collection. Please try again.`,
          description: `Desc: ${e}`,
          status: 'error',
          duration: 9000,
          isClosable: true
        });
      }
      return;
    }

    if (isEditing) {
      toast({
        title: `Collection name was changed to ${values.title}`,
        status: 'info',
        duration: 9000,
        isClosable: true
      });
    } else {
      toast({
        title: (
          <>
            Collection created successfully! Your {pluralize('summary', cards?.length)}{' '}
            {pluralize('has', cards?.length)} been added. To edit or view details, go to the{' '}
            <StyledLink
              color='surface.surface '
              to={`/collection/${newCollection?.id}`}
              textDecor='underline'
            >
              Collections page
            </StyledLink>
            .
          </>
        ),
        status: 'info',
        duration: 9000,
        isClosable: true
      });
    }
    await queryClient.invalidateQueries({ queryKey: ['initiatives'], refetchType: 'all' });
    disclosure.onClose();
  };

  return (
    <Modal
      {...disclosure}
      variant='classic'
      scrollBehavior='inside'
      onCloseComplete={() => {
        reset();
      }}
    >
      <ModalOverlay />
      <ModalContent maxW='434px'>
        <ModalHeader>
          {isEditing ? (
            <HStack>
              <SFSquareAndPencil
                boxSize={7}
                p={1}
              />
              <Text variant='body-600-md'>Edit Collection Name</Text>
            </HStack>
          ) : (
            <HStack>
              <SFAddCircleIcon
                boxSize={7}
                p={1}
              />
              <Text variant='body-600-md'>New Collection</Text>
            </HStack>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          data-testid={isEditing ? 'edit-collection-name-modal' : 'create-collection-modal'}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              gap={6}
              p={6}
            >
              <Stack gap={3}>
                <HStack>
                  <Text variant='body-700-sm'>Title</Text>
                  <Spacer />
                  <Text
                    variant='label-lighter-md'
                    color='surface.outline'
                  >
                    {title?.length} / {maxTitleChars} chars
                  </Text>
                </HStack>
                <Input
                  data-testid='title-input'
                  placeholder='Enter a collection name'
                  textStyle='body-reg-sm'
                  _placeholder={{
                    textStyle: 'body-reg-sm',
                    color: 'surface.outline'
                  }}
                  {...register('title', {
                    required: 'Please enter a collection name.',
                    maxLength: {
                      value: maxTitleChars,
                      message: `Title cannot be more than ${maxTitleChars} characters.`
                    }
                  })}
                  maxLength={maxTitleChars}
                />
                <Collapse in={!!errors.title}>
                  <Text
                    color='critical.primary'
                    fontSize='sm'
                  >
                    {errors?.title?.message}
                  </Text>
                </Collapse>
              </Stack>
              <Button
                data-testid='submit-button'
                placeSelf='end'
                type='submit'
                variant='reskinPrimary'
                bgColor='brandPrimary.onContainer'
                _hover={{ bgColor: 'brandPrimary.onSurface' }}
                isLoading={isCreatingInitiative || isPatchingInitiative || isSettingCardCollections}
              >
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateCollectionModal;
